import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import { roleService, Permission, Role } from '../services/roleService';

interface AddEditRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  role?: Role | null;
}

const AddEditRoleDialog: React.FC<AddEditRoleDialogProps> = ({
  open,
  onClose,
  onSuccess,
  role,
}) => {
  const [name, setName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!role;

  useEffect(() => {
    if (open) {
      loadPermissions();
      if (role) {
        setName(role.name);
        setSelectedPermissions(role.permissions || []);
      } else {
        setName('');
        setSelectedPermissions([]);
      }
      setError(null);
    }
  }, [open, role]);

  const loadPermissions = async () => {
    try {
      const permissions = await roleService.getAllPermissions();
      setAllPermissions(permissions);
    } catch (err) {
      setError('Nem sikerült betölteni a jogosultságokat');
    }
  };

  const handlePermissionToggle = (permissionName: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionName)
        ? prev.filter((p) => p !== permissionName)
        : [...prev, permissionName]
    );
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('A szerepkör neve kötelező');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode && role) {
        await roleService.updateRole(role.id, {
          name,
          permissions: selectedPermissions,
        });
      } else {
        await roleService.createRole({
          name,
          permissions: selectedPermissions,
        });
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(
        err?.message ||
          `Hiba történt a szerepkör ${isEditMode ? 'frissítése' : 'létrehozása'} során`
      );
    } finally {
      setLoading(false);
    }
  };

  // Jogosultságok csoportosítása kategóriák szerint
  const groupPermissions = () => {
    const groups: { [key: string]: Permission[] } = {
      'Általános': [],
      'Időráfordítások': [],
      'Szabadságok': [],
      'Céges autó': [],
      'Ajánlatok': [],
      'Iktatások': [],
      'Megrendelők': [],
      'Munkaszámok': [],
      'Projekttervezés': [],
      'Felhasználók és szerepek': [],
      'Egyéb': [],
    };

    allPermissions.forEach((perm) => {
      if (perm.name.includes('Dashboard') || perm.name.includes('Reports')) {
        groups['Általános'].push(perm);
      } else if (perm.name.includes('TimeTracking')) {
        groups['Időráfordítások'].push(perm);
      } else if (perm.name.includes('Leaves')) {
        groups['Szabadságok'].push(perm);
      } else if (perm.name.includes('CompanyCar')) {
        groups['Céges autó'].push(perm);
      } else if (perm.name.includes('Offers')) {
        groups['Ajánlatok'].push(perm);
      } else if (perm.name.includes('Registrations')) {
        groups['Iktatások'].push(perm);
      } else if (perm.name.includes('Clients')) {
        groups['Megrendelők'].push(perm);
      } else if (perm.name.includes('WorkNumber') || perm.name.includes('ExportFolyamatlap')) {
        groups['Munkaszámok'].push(perm);
      } else if (perm.name.includes('ProjectPlanning')) {
        groups['Projekttervezés'].push(perm);
      } else if (perm.name.includes('Users') || perm.name.includes('Roles')) {
        groups['Felhasználók és szerepek'].push(perm);
      } else {
        groups['Egyéb'].push(perm);
      }
    });

    return groups;
  };

  const permissionGroups = groupPermissions();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Szerepkör szerkesztése' : 'Új szerepkör létrehozása'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Szerepkör neve"
          type="text"
          fullWidth
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 3 }}
        />

        <Typography variant="h6" gutterBottom>
          Jogosultságok
        </Typography>

        <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
          {Object.entries(permissionGroups).map(([groupName, permissions]) => {
            if (permissions.length === 0) return null;
            
            return (
              <Box key={groupName} sx={{ mb: 2 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#ff8c42',
                    mb: 1 
                  }}
                >
                  {groupName}
                </Typography>
                <FormGroup>
                  {permissions.map((permission) => (
                    <FormControlLabel
                      key={permission.name}
                      control={
                        <Checkbox
                          checked={selectedPermissions.includes(permission.name)}
                          onChange={() => handlePermissionToggle(permission.name)}
                          sx={{
                            color: '#ff8c42',
                            '&.Mui-checked': {
                              color: '#ff8c42',
                            },
                          }}
                        />
                      }
                      label={permission.description}
                    />
                  ))}
                </FormGroup>
                <Divider sx={{ mt: 1 }} />
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Mégse
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: '#ff8c42',
            '&:hover': {
              backgroundColor: '#e67a35',
            },
          }}
        >
          {loading ? 'Folyamatban...' : isEditMode ? 'Mentés' : 'Létrehozás'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditRoleDialog;
